import { Upload } from "@carbon/icons-react";
import { Button, Tab, TabList, TabPanels, Tabs } from "@carbon/react";
import React, { useState } from "react";
import { Outlet } from "react-router-dom";

import { AuthorizedItem } from "src/authentication/AuthorizedMenu.tsx";
import { Scope, ScopeResource } from "src/authentication/PermissionsProvider.tsx";
import Loading from "src/components/DataStates/Loading.tsx";
import InlineError from "src/components/Errors/InlineError.tsx";
import { ModalTransition } from "src/components/Modal";
import PageHeader from "src/components/PageHeader.tsx";
import PageStack from "src/components/PageStack/PageStack.tsx";
import { useTabNavigation } from "src/components/TabbedView/useTabNavigation.ts";
import ImportModal from "src/features/import/ImportModal.tsx";
import { useGetBlacklistSchemasQuery } from "src/graphql/anti-fraud/queries/blacklists/BlacklistSchemasQuery.ts";
import { useT } from "src/internationalization";

const BlacklistsPage: React.FC = () => {
  const t = useT();
  const [showImportListsModal, setShowImportListsModal] = useState(false);

  const {
    data: blacklistSchemas,
    error,
    loading,
    hasError,
    hasData,
  } = useGetBlacklistSchemasQuery();

  const tabs = blacklistSchemas.map((listSchema) => ({
    code: listSchema.code,
    label: listSchema.name,
  }));

  const { navigateTab, selectedTab } = useTabNavigation({
    tabs,
    loadingData: loading,
    pathPrefix: "/blacklists",
    getSearch: (searchParams) => searchParams.toString(),
  });

  const onTabChange = (state: { selectedIndex: number }) => navigateTab(state.selectedIndex);

  return (
    <>
      <PageStack>
        <div className="flex justify-between">
          <PageHeader>{t("page.blacklists.title")}</PageHeader>
          <AuthorizedItem
            requiredPermissions={[
              { resource: ScopeResource.BLACKLIST_ELEMENT, scopes: [Scope.IMPORT] },
            ]}
          >
            <Button renderIcon={Upload} onClick={() => setShowImportListsModal(true)}>
              {t("import.button.import")}
            </Button>
          </AuthorizedItem>
        </div>

        {loading && <Loading />}
        {hasError && <InlineError message={error?.message ?? ""} type={error?.type} />}
        {hasData && (
          <Tabs selectedIndex={selectedTab?.index ?? 0} onChange={onTabChange}>
            <TabList aria-label="List of tabs">
              {tabs.map((tab) => (
                <Tab key={tab.code}>{tab.label}</Tab>
              ))}
            </TabList>
            <TabPanels>
              <div className="mt-5">
                <Outlet />
              </div>
            </TabPanels>
          </Tabs>
        )}
      </PageStack>
      <ModalTransition>
        {showImportListsModal && (
          <ImportModal
            importSpecType={"blacklist"}
            onClose={() => {
              setShowImportListsModal(false);
            }}
          />
        )}
      </ModalTransition>
    </>
  );
};

export default BlacklistsPage;
